/**
 * Chat sounds, synthesized with the Web Audio API (no audio files, nothing to license).
 *
 * - send: a short, soft rising "pop" (~120 ms);
 * - receive: a gentle two-note chime (~300 ms), at most one per burst (1.5 s).
 *
 * Browsers keep audio blocked until the first user gesture: the context is created
 * (or resumed) on the first pointer or key event, and until then nothing plays.
 * Only one tab rings: the one focused most recently (kept in localStorage).
 */

const PREF_KEY = (profileId: string | null) => `memberfy:chat-sounds:${profileId || ''}`
const ACTIVE_TAB_KEY = 'memberfy:chat-active-tab'
const RECEIVE_DEBOUNCE_MS = 1500
const VOLUME = 0.12

const tabId = Math.random().toString(36).slice(2)
let ctx: AudioContext | null = null
let unlocked = false
let lastReceiveAt = 0

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key) } catch { return null }
}
const safeSet = (key: string, value: string) => {
  try { localStorage.setItem(key, value) } catch { /* per-browser convenience only */ }
}

export const chatSoundsEnabled = (profileId: string | null): boolean => safeGet(PREF_KEY(profileId)) !== 'off'
export const setChatSoundsEnabled = (profileId: string | null, on: boolean) => safeSet(PREF_KEY(profileId), on ? 'on' : 'off')

const markActiveTab = () => safeSet(ACTIVE_TAB_KEY, tabId)
const isActiveTab = () => {
  if (typeof document !== 'undefined' && document.hasFocus()) return true
  const stored = safeGet(ACTIVE_TAB_KEY)
  return !stored || stored === tabId
}

const audio = (): AudioContext | null => {
  if (!unlocked) return null
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    try { ctx = new Ctor() } catch { return null }
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  return ctx
}

let installed = false
/** Wires the unlock-on-first-gesture and the "most recent tab" marker. Safe to call more than once. */
export const installChatSounds = () => {
  if (installed || typeof window === 'undefined') return
  installed = true
  const unlock = () => {
    unlocked = true
    audio()
    markActiveTab()
  }
  window.addEventListener('pointerdown', unlock, { capture: true, passive: true })
  window.addEventListener('keydown', unlock, { capture: true })
  window.addEventListener('focus', markActiveTab)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') markActiveTab() })
  if (document.hasFocus()) markActiveTab()
}

/** One note: a sine with a quick attack and an exponential decay (no clicks, no clipping). */
const tone = (ac: AudioContext, start: number, from: number, to: number, duration: number, peak: number) => {
  const osc = ac.createOscillator()
  const gain = ac.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(from, start)
  if (to !== from) osc.frequency.exponentialRampToValueAtTime(to, start + duration * 0.8)
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  osc.connect(gain).connect(ac.destination)
  osc.start(start)
  osc.stop(start + duration + 0.02)
}

export const playSendSound = () => {
  const ac = audio()
  if (!ac) return
  tone(ac, ac.currentTime, 420, 760, 0.12, VOLUME)
}

/** Returns false when it did not ring (debounced, another tab, audio still blocked). */
export const playReceiveSound = (): boolean => {
  const now = Date.now()
  if (now - lastReceiveAt < RECEIVE_DEBOUNCE_MS || !isActiveTab()) return false
  const ac = audio()
  if (!ac) return false
  lastReceiveAt = now
  const t0 = ac.currentTime
  tone(ac, t0, 880, 880, 0.18, VOLUME * 0.9)
  tone(ac, t0 + 0.11, 1318.5, 1318.5, 0.19, VOLUME * 0.8)
  return true
}

/** F-107: the Drops countdown, a soft short tick (higher on the last one). The caller checks the preference. */
export const playTickSound = (last = false) => {
  const ac = audio()
  if (!ac) return
  tone(ac, ac.currentTime, last ? 1320 : 990, last ? 1320 : 990, 0.07, VOLUME * 0.6)
}
